import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, fmtUsd } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Swords, Trophy, Check, Calendar, ExternalLink, Timer, Play, Pause, RotateCcw, ScrollText, Share2 } from "lucide-react";
import { toast } from "sonner";

const L = {
  en: {
    title: "Debate arena", badge: "Fund the conversation",
    sub: (w, lo, p) => `Fans fund a debate between two streamers. If the challenge is accepted, the pot goes into escrow. After the debate, the audience votes: the winner takes ${w}% of the pot, the loser ${lo}%, and StreameyeQ keeps a ${p}% fee.`,
    create: "Challenge a streamer", topic: "Debate topic", opponent: "Opponent",
    select: "Select a streamer...", submit: "Send challenge", created: "Challenge sent!",
    proposed: "Waiting for opponent", accepted: "In progress", completed: "Completed", declined: "Declined",
    refunded: "Refunded", refunding: "Refunding",
    noVotesRefund: "The debate didn't happen (no audience votes). All contributions were automatically refunded to the fans.",
    pot: "Pot", fund: "Fund this debate:", vote: "Vote for the winner:",
    voted: "Vote registered!", accept: "Accept", decline: "Decline",
    resolve: "Close poll & split pot", winner: "Winner", tie: (each, p) => `Tie — pot split ${each}/${each} (${p}% fee)`,
    topicPh: "e.g. Is skill or luck more important in gaming?",
    empty: "No debates yet. Be the first to throw the gauntlet!",
    login: "Log in to participate",
    disclaimer: "Debates are organized independently by the streamers. StreameyeQ only manages escrow and the audience poll. Platforms mentioned are independent companies not affiliated with StreameyeQ; earnings are not guaranteed. The rules are an agreement between the streamers to keep the debate respectful; moderation is at the host streamer's discretion.",
    vs: "vs",
    setSchedule: "Set date & stream link", scheduleDate: "Debate date & time",
    scheduleUrl: "Live stream link", saveSchedule: "Save schedule",
    scheduleSaved: "Schedule saved!", scheduledFor: "Live on", watchLive: "Watch live",
    editSchedule: "Edit date & link",
    autoCloseLabel: "Auto-close & pay out (optional)",
    autoCloseHint: "The poll closes and the pot is distributed automatically at this time — no manual action needed.",
    autoCloseInfo: "Closes automatically on",
    rulesLabel: "Debate rules (optional)",
    rulesPh: "e.g. No insults. Respect each speaking turn. Stay on topic...",
    timerToggle: "Enable 1-minute turns",
    rulesTitle: "Rules", acceptRules: "I accept the debate rules",
    mustAccept: "You must accept the rules first",
    timerTitle: "1-minute turns", tStart: "Start", tPause: "Pause", tReset: "Reset",
    rulesLabel: "Debate rules — agreement (optional)",
    editRules: "Edit rules", saveRules: "Save rules", rulesSaved: "Rules updated!",
    overlayBtn: "Timer overlay for OBS/Streamlabs", overlayCopied: "Overlay link copied — paste it as a Browser Source in OBS/Streamlabs",
    share: "Share", shareCopied: "Debate link copied — share it so fans can fund it!",
  },
  es: {
    title: "Arena de debates", badge: "Financia la conversación",
    sub: (w, lo, p) => `Los fans financian un debate entre dos streamers. Si el reto es aceptado, el bote queda en custodia. Tras el debate, la audiencia vota: el ganador se lleva el ${w}% del bote, el perdedor el ${lo}%, y StreameyeQ retiene una comisión del ${p}%.`,
    create: "Retar a un streamer", topic: "Tema del debate", opponent: "Oponente",
    select: "Selecciona un streamer...", submit: "Enviar reto", created: "¡Reto enviado!",
    proposed: "Esperando al oponente", accepted: "En curso", completed: "Completado", declined: "Rechazado",
    refunded: "Reembolsado", refunding: "Reembolsando",
    noVotesRefund: "El debate no ocurrió (sin votos de la audiencia). Todas las aportaciones se reembolsaron automáticamente a los fans.",
    pot: "Bote", fund: "Financia este debate:", vote: "Vota por el ganador:",
    voted: "¡Voto registrado!", accept: "Aceptar", decline: "Rechazar",
    resolve: "Cerrar encuesta y repartir bote", winner: "Ganador", tie: (each, p) => `Empate — bote ${each}/${each} (${p}% comisión)`,
    topicPh: "ej: ¿Importa más la habilidad o la suerte en los videojuegos?",
    empty: "Aún no hay debates. ¡Sé el primero en lanzar el guante!",
    login: "Inicia sesión para participar",
    disclaimer: "Los debates son organizados de forma independiente por los streamers. StreameyeQ solo gestiona la custodia y la encuesta de la audiencia. Las plataformas mencionadas son empresas independientes sin relación con StreameyeQ; los ingresos no están garantizados. Las reglas son un acuerdo entre los streamers para mantener el debate respetuoso; la moderación queda a criterio del streamer anfitrión.",
    vs: "vs",
    setSchedule: "Fijar fecha y enlace del directo", scheduleDate: "Fecha y hora del debate",
    scheduleUrl: "Enlace del directo", saveSchedule: "Guardar",
    scheduleSaved: "¡Fecha guardada!", scheduledFor: "En directo el", watchLive: "Ver el directo",
    editSchedule: "Editar fecha y enlace",
    autoCloseLabel: "Cierre y reparto automático (opcional)",
    autoCloseHint: "La encuesta se cierra y el bote se reparte automáticamente a esta hora, sin intervención manual.",
    autoCloseInfo: "Se cierra automáticamente el",
    rulesLabel: "Reglas del debate (opcional)",
    rulesPh: "ej: Sin insultos. Respeta el turno de palabra. Cíñete al tema...",
    timerToggle: "Activar turnos de 1 minuto",
    rulesTitle: "Reglas", acceptRules: "Acepto las reglas del debate",
    mustAccept: "Primero debes aceptar las reglas",
    timerTitle: "Turnos de 1 minuto", tStart: "Iniciar", tPause: "Pausar", tReset: "Reiniciar",
    rulesLabel: "Reglas del debate — acuerdo (opcional)",
    editRules: "Editar reglas", saveRules: "Guardar reglas", rulesSaved: "¡Reglas actualizadas!",
    overlayBtn: "Overlay del cronómetro para OBS/Streamlabs", overlayCopied: "Enlace del overlay copiado — pégalo como Browser Source en OBS/Streamlabs",
    share: "Compartir", shareCopied: "¡Enlace del debate copiado — compártelo para que los fans lo financien!",
  },
};

const AMOUNTS = [5, 10, 25, 50, 100];

function SpeakerTimer({ name, color, l }) {
  const [secs, setSecs] = useState(60);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) { setRunning(false); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);
  const done = secs <= 0;
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div data-testid="speaker-timer" className={`flex-1 rounded-xl border p-4 ${done ? "border-red-700/60 bg-red-950/20" : "border-zinc-800 bg-zinc-950/60"}`}>
      <p className="text-xs font-semibold text-zinc-400 mb-1 truncate">{name}</p>
      <p className={`text-3xl font-black tabular-nums ${done ? "text-red-400" : color}`}>{mm}:{ss}</p>
      <div className="flex gap-1.5 mt-3">
        <Button data-testid="timer-toggle-btn" size="sm" disabled={done}
          className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs px-3"
          onClick={() => setRunning((r) => !r)}>
          {running ? <Pause size={12} className="mr-1" /> : <Play size={12} className="mr-1" />}
          {running ? l.tPause : l.tStart}
        </Button>
        <Button data-testid="timer-reset-btn" size="sm" variant="outline"
          className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 bg-transparent h-7 text-xs px-3"
          onClick={() => { setSecs(60); setRunning(false); }}>
          <RotateCcw size={12} className="mr-1" /> {l.tReset}
        </Button>
      </div>
    </div>
  );
}


export default function Debates() {
  const { lang } = useLang();
  const l = L[lang] || L.en;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("d");
  const [debates, setDebates] = useState(null);
  const [streamers, setStreamers] = useState([]);
  const [form, setForm] = useState({ topic: "", opponent_id: "", rules: "", turn_timer_enabled: false });
  const [busy, setBusy] = useState(false);
  const [split, setSplit] = useState({ winner: 70, loser: 20 });
  const [schedOpen, setSchedOpen] = useState({});
  const [schedForm, setSchedForm] = useState({});
  const [rulesOpen, setRulesOpen] = useState({});
  const [rulesForm, setRulesForm] = useState({});
  const [acceptedRules, setAcceptedRules] = useState({});
  const platformPct = Math.max(0, 100 - split.winner - split.loser);
  const [myVotes, setMyVotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("liveq_debate_votes") || "{}"); } catch { return {}; }
  });

  const registerVote = (id, choice) => {
    const next = { ...myVotes, [id]: choice };
    setMyVotes(next);
    localStorage.setItem("liveq_debate_votes", JSON.stringify(next));
  };

  const load = useCallback(() => {
    api.get("/debates").then(({ data }) => setDebates(data)).catch(() => setDebates([]));
  }, []);

  useEffect(() => {
    load();
    api.get("/streamers").then(({ data }) => setStreamers(data)).catch(() => {});
    api.get("/config/requirements").then(({ data }) =>
      setSplit({ winner: data.debate_winner_percent, loser: data.debate_loser_percent })).catch(() => {});
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (focusId && debates && debates.length) {
      const el = document.getElementById(`debate-${focusId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [debates, focusId]);

  const requireLogin = () => {
    if (!user || user === false) { toast.info(l.login); navigate("/auth"); return true; }
    return false;
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const createDebate = (e) => {
    e.preventDefault();
    if (requireLogin()) return;
    act(async () => {
      await api.post("/debates", form);
      setForm({ topic: "", opponent_id: "", rules: "", turn_timer_enabled: false });
    }, l.created);
  };

  const contribute = async (debateId, usd) => {
    if (requireLogin()) return;
    if (!acceptedRules[`fund_${debateId}`]) { toast.error(l.mustAccept); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/debates/contribute", { debate_id: debateId, amount_usd: usd, origin_url: window.location.origin, accept_rules: true });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const saveSchedule = (d) => {
    if (requireLogin()) return;
    const f = schedForm[d.id] || {};
    if (!f.date || !f.url) { toast.error(l.saveSchedule); return; }
    act(async () => {
      await api.post(`/debates/${d.id}/schedule`, {
        scheduled_at: new Date(f.date).toISOString(), stream_url: f.url.trim(),
        auto_close_at: f.autoClose ? new Date(f.autoClose).toISOString() : "",
      });
      setSchedOpen((o) => ({ ...o, [d.id]: false }));
    }, l.scheduleSaved);
  };

  const fmtSched = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "es" ? "es-ES" : "en-US",
        { dateStyle: "long", timeStyle: "short" });
    } catch { return iso; }
  };

  const saveRules = (d) => {
    if (requireLogin()) return;
    act(async () => {
      await api.post(`/debates/${d.id}/rules`, { rules: (rulesForm[d.id] ?? "").trim() });
      setRulesOpen((o) => ({ ...o, [d.id]: false }));
    }, l.rulesSaved);
  };

  const copyOverlay = (d) => {
    const url = `${window.location.origin}/overlay/timer/${d.id}`;
    const done = () => toast.success(l.overlayCopied);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(() => window.open(url, "_blank"));
    else { window.open(url, "_blank"); }
  };

  const shareDebate = (id) => {
    const url = `${window.location.origin}/debates?d=${id}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(() => toast.success(l.shareCopied)).catch(() => toast.info(url));
    else toast.info(url);
  };


  const statusBadge = (s) => {
    const map = {
      proposed: ["bg-amber-500/15 text-amber-400 border-amber-700/50", l.proposed],
      accepted: ["bg-emerald-500/15 text-emerald-400 border-emerald-700/50", l.accepted],
      completed: ["bg-zinc-700/40 text-zinc-300 border-zinc-700", l.completed],
      refunded: ["bg-red-500/15 text-red-400 border-red-800/50", l.refunded],
      refunding: ["bg-amber-500/15 text-amber-400 border-amber-700/50", l.refunding],
    };
    const [cls, label] = map[s] || map.proposed;
    return <Badge variant="outline" className={`${cls} text-xs`}>{label}</Badge>;
  };

  const isStreamer = user && user !== false && user.role === "streamer";

  return (
    <div data-testid="debates-page" className="max-w-5xl mx-auto px-6 py-14">
      <span className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400">{l.badge}</span>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mt-3 mb-4">{l.title}</h1>
      <p className="text-sm text-zinc-400 max-w-2xl mb-12">{l.sub(split.winner, split.loser, platformPct)}</p>

      {isStreamer && (
        <form onSubmit={createDebate} data-testid="create-debate-form" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8 mb-12 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-zinc-300 mb-2">{l.topic}</label>
            <Input data-testid="debate-topic-input" required minLength={5} maxLength={140} value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white" placeholder={l.topicPh} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-zinc-300 mb-2">{l.opponent}</label>
            <select data-testid="debate-opponent-select" required value={form.opponent_id}
              onChange={(e) => setForm({ ...form, opponent_id: e.target.value })}
              className="w-full h-10 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm text-white">
              <option value="">{l.select}</option>
              {streamers.filter((s) => s.user_id !== user.id).map((s) => (
                <option key={s.user_id} value={s.user_id}>{s.name} ({s.channel.platform})</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-zinc-300 mb-2">{l.rulesLabel}</label>
            <textarea data-testid="debate-rules-input" maxLength={1000} rows={2} value={form.rules}
              onChange={(e) => setForm({ ...form, rules: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white resize-none"
              placeholder={l.rulesPh} />
          </div>
          <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none">
            <input data-testid="debate-timer-toggle" type="checkbox" checked={form.turn_timer_enabled}
              onChange={(e) => setForm({ ...form, turn_timer_enabled: e.target.checked })}
              className="h-4 w-4 accent-emerald-500" />
            <span className="text-sm text-zinc-300 inline-flex items-center gap-1.5"><Timer size={14} className="text-emerald-400" />{l.timerToggle}</span>
          </label>
          <Button data-testid="create-debate-btn" type="submit" disabled={busy}
            className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white md:col-span-3">
            <Swords size={15} className="mr-2" /> {l.submit}
          </Button>
        </form>
      )}

      {debates === null ? (
        <p className="text-zinc-500">...</p>
      ) : debates.length === 0 ? (
        <p className="text-zinc-500">{l.empty}</p>
      ) : (
        <div className="space-y-6">
          {debates.map((d) => {
            const total = d.votes_a + d.votes_b;
            const pa = total ? Math.round((d.votes_a / total) * 100) : 50;
            const isB = user && user !== false && user.id === d.streamer_b_id;
            const isParticipant = user && user !== false && (user.id === d.streamer_a_id || user.id === d.streamer_b_id || user.role === "admin");
            return (
              <div key={d.id} id={`debate-${d.id}`} data-testid="debate-card"
                className={`bg-zinc-900 border rounded-2xl p-8 transition-colors ${focusId === d.id ? "border-emerald-500/70 ring-1 ring-emerald-500/40" : "border-zinc-800/60"}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className="text-xl font-semibold text-white">{d.topic}</h3>
                  <div className="flex items-center gap-2">
                    {statusBadge(d.status)}
                    <button data-testid="share-debate-btn" onClick={() => shareDebate(d.id)} title={l.share}
                      className="text-zinc-500 hover:text-emerald-400 transition-colors p-1">
                      <Share2 size={15} />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-zinc-400 mb-4">
                  <span className="text-white font-semibold">{d.streamer_a_name}</span> {l.vs} <span className="text-white font-semibold">{d.streamer_b_name}</span>
                  <span className="ml-4 text-emerald-400 font-bold">{l.pot}: {fmtUsd(d.pot_cents)}</span>
                </p>

                {d.scheduled_at && (
                  <div data-testid="debate-schedule-info" className="flex flex-col gap-2 mb-4 bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <Calendar size={16} className="text-emerald-400 shrink-0" />
                      <span className="text-sm text-zinc-300">{l.scheduledFor} <span className="font-semibold text-white">{fmtSched(d.scheduled_at)}</span></span>
                      {d.stream_url && (
                        <a data-testid="debate-watch-link" href={d.stream_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 ml-auto">
                          <ExternalLink size={13} /> {l.watchLive}
                        </a>
                      )}
                    </div>
                    {d.auto_close_at && !["completed", "refunded"].includes(d.status) && (
                      <div data-testid="debate-autoclose-info" className="flex items-center gap-2 text-xs text-amber-400/90">
                        <Timer size={13} className="shrink-0" />
                        <span>{l.autoCloseInfo} <span className="font-semibold">{fmtSched(d.auto_close_at)}</span></span>
                      </div>
                    )}
                  </div>
                )}

                {(d.rules || (isParticipant && d.status !== "completed")) && (
                  <div data-testid="debate-rules" className="mb-4 bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 inline-flex items-center gap-1.5"><ScrollText size={13} className="text-emerald-400" />{l.rulesTitle}</p>
                      {isParticipant && (d.status === "proposed" || d.status === "accepted") && !rulesOpen[d.id] && (
                        <button data-testid="edit-rules-btn" onClick={() => { setRulesForm((s) => ({ ...s, [d.id]: d.rules || "" })); setRulesOpen((o) => ({ ...o, [d.id]: true })); }}
                          className="text-xs font-semibold text-emerald-400 hover:text-emerald-300">{l.editRules}</button>
                      )}
                    </div>
                    {rulesOpen[d.id] ? (
                      <div data-testid="edit-rules-form" className="mt-1">
                        <textarea data-testid="edit-rules-input" maxLength={1000} rows={2} value={rulesForm[d.id] ?? ""}
                          onChange={(e) => setRulesForm((s) => ({ ...s, [d.id]: e.target.value }))}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white resize-none"
                          placeholder={l.rulesPh} />
                        <Button data-testid="save-rules-btn" size="sm" disabled={busy}
                          className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs mt-2"
                          onClick={() => saveRules(d)}>{l.saveRules}</Button>
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-300 whitespace-pre-line">{d.rules || <span className="text-zinc-600">—</span>}</p>
                    )}
                  </div>
                )}

                {d.turn_timer_enabled && d.status === "accepted" && (
                  <div data-testid="debate-timers" className="mb-5">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 inline-flex items-center gap-1.5"><Timer size={13} className="text-emerald-400" />{l.timerTitle}</p>
                      {isParticipant && (
                        <button data-testid="overlay-copy-btn" onClick={() => copyOverlay(d)}
                          className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1.5">
                          <ExternalLink size={12} /> {l.overlayBtn}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <SpeakerTimer name={d.streamer_a_name} color="text-emerald-400" l={l} />
                      <SpeakerTimer name={d.streamer_b_name} color="text-cyan-400" l={l} />
                    </div>
                  </div>
                )}

                {total > 0 && (
                  <div className="mb-5">
                    <div className="flex justify-between text-xs text-zinc-400 mb-1">
                      <span>{d.streamer_a_name} · {pa}% ({d.votes_a})</span>
                      <span>{d.streamer_b_name} · {100 - pa}% ({d.votes_b})</span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-800 overflow-hidden flex">
                      <div className="bg-emerald-500 h-full" style={{ width: `${pa}%` }} />
                      <div className="bg-cyan-500 h-full" style={{ width: `${100 - pa}%` }} />
                    </div>
                  </div>
                )}

                {d.status === "refunded" && (
                  <div data-testid="debate-refunded-result" className="bg-red-950/30 border border-red-800/50 rounded-xl p-4 mb-4 flex items-center gap-3">
                    <RotateCcw size={18} className="text-red-400 shrink-0" />
                    <p className="text-sm text-red-300">{l.noVotesRefund}</p>
                  </div>
                )}

                {d.status === "completed" && (
                  <div data-testid="debate-result" className="bg-emerald-950/30 border border-emerald-800/50 rounded-xl p-4 mb-4 flex items-center gap-3">
                    <Trophy size={18} className="text-emerald-400" />
                    <p className="text-sm text-emerald-300">
                      {d.tie
                        ? l.tie(Math.round((100 - platformPct) / 2), platformPct)
                        : <>{l.winner}: <span className="font-bold">{d.winner_id === d.streamer_a_id ? d.streamer_a_name : d.streamer_b_name}</span> · {fmtUsd(d.winner_amount_cents)} ({d.winner_percent ?? (d.pot_cents ? Math.round((d.winner_amount_cents / d.pot_cents) * 100) : split.winner)}%) / {fmtUsd(d.loser_amount_cents)} ({d.loser_percent ?? (d.pot_cents ? Math.round((d.loser_amount_cents / d.pot_cents) * 100) : split.loser)}%)</>}
                    </p>
                  </div>
                )}

                {d.status === "accepted" && !(user && user !== false && (user.id === d.streamer_a_id || user.id === d.streamer_b_id)) && (
                  <div className="mb-3">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
                      <input data-testid="fund-accept-rules" type="checkbox" checked={!!acceptedRules[`fund_${d.id}`]}
                        onChange={(e) => setAcceptedRules((a) => ({ ...a, [`fund_${d.id}`]: e.target.checked }))}
                        className="h-4 w-4 accent-emerald-500" />
                      <span className="text-xs text-zinc-400">{l.acceptRules}</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-zinc-500 mr-1">{l.fund}</span>
                      {AMOUNTS.map((a) => (
                        <Button key={a} data-testid="debate-fund-btn" size="sm" variant="outline" disabled={busy || !acceptedRules[`fund_${d.id}`]}
                          className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 bg-transparent h-8 text-xs disabled:opacity-40"
                          onClick={() => contribute(d.id, a)}>
                          ${a}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {d.status === "accepted" && !(user && user !== false && (user.id === d.streamer_a_id || user.id === d.streamer_b_id)) && (
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-xs text-zinc-500 mr-1">{l.vote}</span>
                    <Button data-testid="vote-a-btn" size="sm" disabled={busy}
                      className={`rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs ${myVotes[d.id] === "a" ? "ring-2 ring-white/70" : ""}`}
                      onClick={() => { if (!requireLogin()) act(async () => { await api.post(`/debates/${d.id}/vote`, { choice: "a" }); registerVote(d.id, "a"); }, l.voted); }}>
                      {myVotes[d.id] === "a" && <Check size={13} className="mr-1" />}{d.streamer_a_name}
                    </Button>
                    <Button data-testid="vote-b-btn" size="sm" disabled={busy}
                      className={`rounded-full bg-cyan-600 hover:bg-cyan-500 text-white h-8 text-xs ${myVotes[d.id] === "b" ? "ring-2 ring-white/70" : ""}`}
                      onClick={() => { if (!requireLogin()) act(async () => { await api.post(`/debates/${d.id}/vote`, { choice: "b" }); registerVote(d.id, "b"); }, l.voted); }}>
                      {myVotes[d.id] === "b" && <Check size={13} className="mr-1" />}{d.streamer_b_name}
                    </Button>
                  </div>
                )}

                {d.status === "proposed" && isB && (
                  <div>
                    <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
                      <input data-testid="accept-rules-checkbox" type="checkbox" checked={!!acceptedRules[`accept_${d.id}`]}
                        onChange={(e) => setAcceptedRules((a) => ({ ...a, [`accept_${d.id}`]: e.target.checked }))}
                        className="h-4 w-4 accent-emerald-500" />
                      <span className="text-xs text-zinc-400">{l.acceptRules}</span>
                    </label>
                    <div className="flex gap-2">
                      <Button data-testid="debate-accept-btn" size="sm" disabled={busy || !acceptedRules[`accept_${d.id}`]}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs disabled:opacity-40"
                        onClick={() => act(() => api.post(`/debates/${d.id}/respond`, { accept: true, accept_rules: true }))}>
                        {l.accept}
                      </Button>
                      <Button data-testid="debate-decline-btn" size="sm" variant="outline" disabled={busy}
                        className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 bg-transparent h-8 text-xs"
                        onClick={() => act(() => api.post(`/debates/${d.id}/respond`, { accept: false }))}>
                        {l.decline}
                      </Button>
                    </div>
                  </div>
                )}

                {d.status === "accepted" && isParticipant && (
                  <div className="mt-2">
                    {!schedOpen[d.id] ? (
                      <Button data-testid="debate-schedule-btn" size="sm" variant="outline" disabled={busy}
                        className="rounded-full border-emerald-800/60 text-emerald-400 hover:bg-emerald-950/40 bg-transparent h-8 text-xs"
                        onClick={() => {
                          setSchedForm((s) => ({ ...s, [d.id]: {
                            date: d.scheduled_at ? new Date(d.scheduled_at).toISOString().slice(0, 16) : "",
                            url: d.stream_url || "",
                            autoClose: d.auto_close_at ? new Date(d.auto_close_at).toISOString().slice(0, 16) : "" } }));
                          setSchedOpen((o) => ({ ...o, [d.id]: true }));
                        }}>
                        <Calendar size={13} className="mr-1.5" /> {d.scheduled_at ? l.editSchedule : l.setSchedule}
                      </Button>
                    ) : (
                      <div data-testid="debate-schedule-form" className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-zinc-400 mb-1">{l.scheduleDate}</label>
                          <Input data-testid="debate-schedule-date" type="datetime-local"
                            value={(schedForm[d.id] || {}).date || ""}
                            onChange={(e) => setSchedForm((s) => ({ ...s, [d.id]: { ...(s[d.id] || {}), date: e.target.value } }))}
                            className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-zinc-400 mb-1">{l.scheduleUrl}</label>
                          <Input data-testid="debate-schedule-url" type="url" placeholder="https://..."
                            value={(schedForm[d.id] || {}).url || ""}
                            onChange={(e) => setSchedForm((s) => ({ ...s, [d.id]: { ...(s[d.id] || {}), url: e.target.value } }))}
                            className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-zinc-400 mb-1">{l.autoCloseLabel}</label>
                          <Input data-testid="debate-schedule-autoclose" type="datetime-local"
                            value={(schedForm[d.id] || {}).autoClose || ""}
                            onChange={(e) => setSchedForm((s) => ({ ...s, [d.id]: { ...(s[d.id] || {}), autoClose: e.target.value } }))}
                            className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm" />
                          <p className="text-[11px] text-zinc-500 mt-1">{l.autoCloseHint}</p>
                        </div>
                        <Button data-testid="debate-schedule-save" size="sm" disabled={busy}
                          className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-9 text-xs sm:col-span-2"
                          onClick={() => saveSchedule(d)}>
                          {l.saveSchedule}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {d.status === "accepted" && isParticipant && (
                  <Button data-testid="debate-resolve-btn" size="sm" variant="outline" disabled={busy}
                    className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 bg-transparent h-8 text-xs mt-3"
                    onClick={() => act(() => api.post(`/debates/${d.id}/resolve`))}>
                    <Trophy size={13} className="mr-1.5" /> {l.resolve}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p data-testid="debates-disclaimer" className="text-xs text-zinc-600 mt-12 leading-relaxed">{l.disclaimer}</p>
    </div>
  );
}
